import { useState, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import './WorldMap.css';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const PIN_COLORS = [
  '#9b7dc8', '#5cb88a', '#e8895a', '#5a9ec8',
  '#e06090', '#c8a800', '#5a9ec8', '#9b7dc8',
];

export default function WorldMap({ posts }) {
  const [tooltip, setTooltip] = useState(null);
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });
  const mapRef = useRef(null);

  const located = posts.filter(p => p.lng != null && p.lat != null);

  // Group posts at the same coordinates (within ~1 degree)
  const grouped = [];
  located.forEach(post => {
    const existing = grouped.find(
      g => Math.abs(g.lat - post.lat) < 1.5 && Math.abs(g.lng - post.lng) < 1.5
    );
    if (existing) {
      existing.posts.push(post);
    } else {
      grouped.push({ lat: post.lat, lng: post.lng, posts: [post] });
    }
  });

  function handleZoomIn() {
    setPosition(p => ({ ...p, zoom: Math.min(p.zoom * 1.5, 8) }));
  }
  function handleZoomOut() {
    setPosition(p => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) }));
  }
  function handleReset() {
    setPosition({ coordinates: [0, 20], zoom: 1 });
  }

  return (
    <div className="world-map-wrapper">
      <div className="map-header">
        <h3>🌍 Stories From Around the World</h3>
        <p>
          Kids from <strong>{located.length}</strong> {located.length === 1 ? 'place' : 'places'} across the globe
          have shared their stories. Click any pin to see who shared! 💜
        </p>
      </div>

      <div className="map-container" ref={mapRef}>
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{ scale: 160 }}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup
            zoom={position.zoom}
            center={position.coordinates}
            onMoveEnd={setPosition}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#e8e0f4"
                    stroke="#ffffff"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: '#d0c4ec', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>

            {grouped.map((group, i) => {
              const color = PIN_COLORS[i % PIN_COLORS.length];
              const count = group.posts.length;
              return (
                <Marker
                  key={i}
                  coordinates={[group.lng, group.lat]}
                  onMouseEnter={() => setTooltip(group)}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => setTooltip(t => t === group ? null : group)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Pulse ring */}
                  <circle
                    r={count > 1 ? 14 : 10}
                    fill={color}
                    opacity={0.2}
                    className="pin-pulse"
                  />
                  {/* Pin body */}
                  <circle
                    r={count > 1 ? 9 : 7}
                    fill={color}
                    stroke="white"
                    strokeWidth={2}
                  />
                  {/* Count badge for multiple posts */}
                  {count > 1 && (
                    <text
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="white"
                      fontSize={8}
                      fontWeight="bold"
                      fontFamily="Nunito, sans-serif"
                      style={{ pointerEvents: 'none' }}
                    >
                      {count}
                    </text>
                  )}
                  {/* Heart for single */}
                  {count === 1 && (
                    <text
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={7}
                      style={{ pointerEvents: 'none' }}
                    >
                      💜
                    </text>
                  )}
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div className="map-tooltip">
            <button className="tooltip-close" onClick={() => setTooltip(null)}>✕</button>
            <div className="tooltip-location">
              📍 {tooltip.posts[0].city ? `${tooltip.posts[0].city}, ` : ''}{tooltip.posts[0].country}
            </div>
            {tooltip.posts.map(post => (
              <div key={post.id} className="tooltip-story">
                <div className="tooltip-name">{post.name}</div>
                <div className="tooltip-category">{post.category}</div>
                <p className="tooltip-message">"{post.message.slice(0, 100)}{post.message.length > 100 ? '...' : ''}"</p>
              </div>
            ))}
          </div>
        )}

        {/* Zoom controls */}
        <div className="map-controls">
          <button onClick={handleZoomIn} aria-label="Zoom in" title="Zoom in">＋</button>
          <button onClick={handleZoomOut} aria-label="Zoom out" title="Zoom out">－</button>
          <button onClick={handleReset} aria-label="Reset view" title="Reset" className="reset-btn">⌂</button>
        </div>
      </div>

      {located.length === 0 && (
        <div className="map-empty">
          <span>🌍</span>
          <p>Share your story and add your pin to the world map!</p>
        </div>
      )}
    </div>
  );
}
